import { Loader } from '@huggg/react-components';
import { Huggg } from 'components';
import { PrepaidLoader } from 'components/ui/PrepaidCard/PrepaidLoader';
import dictionary from 'config/dictionary';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  isTilloVoucher,
  tilloVoucherError,
  voucherRedemptionError,
} from 'utilities/errorHandling';
import { isDigitalChequeHuggg } from 'utilities/huggg';
import SegmentRedemption from './Segments/SegmentRedemption';
import styles from './voucher.module.scss';

/**
 * Display the QR Code voucher to the user should they wish to redeem their
 * huggg. Will show segments if any are available.
 *
 * @param {Object} props
 */
const Voucher = (props) => {
  const history = useHistory();

  const {
    hasVoucherToClaim,
    huggg,
    isGenerating,
    isTestHugggRedeemed,
    onSegmentConfirm,
    selectedSegment,
    vouchers,
    voucherError,
    hideProductImage,
    isFormRedemptionType,
    isPin4Type,
    isPrepaidCardType,
    isBankPaymentType,
    defaultTerritory,
    redeemedSegment,
  } = props;

  const hasVouchers = Boolean(vouchers && vouchers.length);
  const hasFulFilVoucher =
    hasVouchers && vouchers.some((voucher) => voucher.type === 'fulfil');
  // If the voucher requires no specific redemption flow, just call claim straight away
  useEffect(() => {
    if (
      (!hasVouchers || hasVoucherToClaim) &&
      selectedSegment &&
      selectedSegment.id &&
      !isFormRedemptionType &&
      !isPin4Type &&
      !isPrepaidCardType &&
      !isBankPaymentType &&
      !hasFulFilVoucher
    ) {
      console.log('Voucher: onSegmentConfirm', {
        selectedSegment,
      });
      onSegmentConfirm(selectedSegment, { territory: defaultTerritory });
    }
  }, [
    hasVouchers,
    hasVoucherToClaim,
    selectedSegment,
    isFormRedemptionType,
    isPin4Type,
    isPrepaidCardType,
    isBankPaymentType,
    defaultTerritory,
    hasFulFilVoucher,
    onSegmentConfirm,
  ]);

  const shouldShowHuggg = !['form-redemption', 'prepaid-card'].includes(
    selectedSegment?.voucherType,
  );

  // On a digital cheque the product title already reads "Digital Cheque - £x",
  // so the merchandising title underneath just repeats it. Replace it with a
  // confirmation of the route the recipient picked. A plain bank payment offers
  // no choice, so it keeps the existing description.
  const chosenSegmentDescription = useMemo(() => {
    if (!isDigitalChequeHuggg(huggg)) return undefined;

    // selectedSegment does not survive a page reload, so fall back to the
    // segment already claimed. If neither is known, leave the description as it
    // is rather than guess the wrong route.
    const segment = selectedSegment ?? redeemedSegment;

    if (segment?.voucherType === 'bank-payment') {
      return dictionary.bankPayment.labels.chosenBankTransfer;
    }

    if (segment?.voucherType === 'post-office') {
      return dictionary.bankPayment.labels.chosenPostOfficeCash;
    }

    return undefined;
  }, [huggg, selectedSegment, redeemedSegment]);

  const getLoader = useCallback(() => {
    return selectedSegment?.voucherType === 'prepaid-card' ? (
      <PrepaidLoader />
    ) : (
      <Loader />
    );
  }, [selectedSegment]);

  const isTillo = isTilloVoucher(selectedSegment?.voucherType);

  const getError = useCallback(() => {
    if (selectedSegment?.voucherType === 'prepaid-card') {
      return null;
    }

    const errorContent = isTillo
      ? tilloVoucherError(huggg.brand_name)
      : voucherRedemptionError();

    return <div className={styles.error}>{errorContent}</div>;
  }, [selectedSegment, isTillo, huggg.brand_name]);

  if (isTestHugggRedeemed) {
    history.push(`/test/${huggg.shortcode}/test-redeemed`);
  }

  return (
    <div data-test-id="voucher-segment">
      {shouldShowHuggg && (
        <div style={{ marginBottom: 20 }}>
          <Huggg
            huggg={huggg}
            hideImage={hideProductImage}
            descriptionOverride={chosenSegmentDescription}
          />
        </div>
      )}

      {voucherError && !isBankPaymentType && getError()}

      {huggg.id && (
        <SegmentRedemption
          huggg={huggg}
          selectedSegment={selectedSegment}
          onConfirm={onSegmentConfirm}
          data-test-id={
            hasVouchers
              ? 'voucher-claimed'
              : 'voucher-segment-redemption-component'
          }
          isFormRedemptionType={isFormRedemptionType}
          isPin4Type={isPin4Type}
          isPrepaidCardType={isPrepaidCardType}
          isBankPaymentType={isBankPaymentType}
          defaultTerritory={defaultTerritory}
          shouldShowRetry={Boolean(voucherError) && !isGenerating && !isTillo}
        />
      )}

      {isGenerating && getLoader()}
    </div>
  );
};

Voucher.propTypes = {
  onSegmentConfirm: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isTestHugggRedeemed: PropTypes.bool.isRequired,
  selectedSegment: PropTypes.object,
  redeemedSegment: PropTypes.object,
  brandLocation: PropTypes.string,
  error: PropTypes.object,
  expiryDate: PropTypes.instanceOf(Date),
  vouchers: PropTypes.array,
  voucherError: PropTypes.object,
  hideProductImage: PropTypes.bool,
  isFormRedemptionType: PropTypes.bool,
  isPin4Type: PropTypes.bool,
  isPrepaidCardType: PropTypes.bool,
  isBankPaymentType: PropTypes.bool,
  defaultTerritory: PropTypes.string,
  onFulfilClick: PropTypes.func,
  hasVoucherToClaim: PropTypes.bool.isRequired,
};

export default Voucher;
